import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Input  from '../../Atoms/Input';
import Select from '../../Atoms/Select';
import codes  from './countryCodes';     // ← static data
import { defineMessages, injectIntl } from 'react-intl';
import './index.sass';

const onlyDigits = s => s.replace(/\D/g, '');

// Transform the country data to Select-compatible format and sort by dial code
const countryOptions = codes.map(country => ({
  value: country.dial_code.replace('+', ''), // Remove '+' for value
  label: `${country.dial_code} (${country.name})`
})).sort((a, b) => {
  // Brazil (+55) first, then all others in numerical order
  if (a.value === '55') return -1;
  if (b.value === '55') return 1;
  
  // Sort remaining codes numerically (convert to numbers for proper comparison)
  return parseInt(a.value, 10) - parseInt(b.value, 10);
});

const splitValue = (phone = '') => {
  if (!phone) return { cc: '55', nn: '' };
  const digits = phone.startsWith('+') ? phone.substring(1) : phone;
  if (!digits) return { cc: '55', nn: '' };
  
  // Try to match against available country codes (longest first) - use a copy to avoid modifying original
  const sortedCodes = [...countryOptions].sort((a, b) => b.value.length - a.value.length);
  const matchedCode = sortedCodes.find(o => digits.startsWith(o.value));
  if (matchedCode) {
    return { cc: matchedCode.value, nn: digits.substring(matchedCode.value.length) };
  }
  
  // Fallback: assume first 1-3 digits are country code
  const match = digits.match(/^(\d{1,3})(\d*)$/);
  return match ? { cc: match[1], nn: match[2] } : { cc: '55', nn: '' };
};

const CustomerPhone = ({ id, value, onChange, intl }) => {
  const [{ cc, nn }, setParts] = useState(() => splitValue(value));

  // keep in sync if parent overwrites value
  useEffect(() => {
    const parts = splitValue(value);
    if (parts.cc !== cc || parts.nn !== nn) setParts(parts);
  }, [value]);                                 // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (newCC, newNN) => {
    const e164 = newNN ? `+${newCC}${newNN}` : '';
    if (e164 !== value) onChange(e164);
  };

  const { formatMessage } = intl;
	const messages = defineMessages({
		code: {
			id: 'customerPhone.code',
			defaultMessage: 'Código do país'
		},
    phone: {
      id: 'customerPhone.phone',
      defaultMessage: 'Telefone'
    }
  });

  return (
    <div className="CustomerPhone">
      <Select
        id={`${id}_cc`}
        className="CustomerPhone__country"
        label={formatMessage(messages.code)}
        value={cc}
        options={countryOptions}
        onChange={e => {
          const newCC = e.target.value;
          setParts(p => ({ ...p, cc: newCC }));
          emit(newCC, nn);
        }}
      />
      <Input
        id={id}
        name="phone"
        autoComplete="tel-national"
        type="tel"
        className="CustomerPhone__input"
        label={formatMessage(messages.phone)}
        placeholder="11912345678"
        value={nn}
        maxLength={15}
        onChange={e => {
          const newNN = onlyDigits(e.target.value);
          setParts(p => ({ ...p, nn: newNN }));
          emit(cc, newNN);
        }}
        required
      />
    </div>
  );
};

CustomerPhone.propTypes = {
  id:    PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};
CustomerPhone.defaultProps = { id: 'customer_phone', value: '', onChange: () => {} };

export default injectIntl(CustomerPhone); 